import { Assert } from "./Assert";
import { Constants } from "./Constants";
import { StringHelper } from "./Helpers/StringHelper";
import { NAMESPACE } from "./_Namespace";

function truncateMilliseconds(value: number, length: 1 | 2 | 3 | 4 | 5 | 6 | 7)
{
    const
        value7Digits = parseInt(`${value}`.padEnd(7, "0").substring(0, 7)),
        divisor = parseInt("1000000".substring(0, 8 - length));

    return Math.floor(value7Digits / divisor);
}
function formatMilliseconds(value: number, length: 1 | 2 | 3 | 4 | 5 | 6 | 7)
{
    return truncateMilliseconds(value, length).toFixed().padEnd(length, "0");
}

export enum DateTimeInfoKind
{
    local = "local",
    utc = "utc",
}

export class DateTimeInfo
{
    // #region Static

    public static classNamespace = NAMESPACE + "DateTimeInfo";
    public static className = "DateTimeInfo";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Public Methods

    public static isDateString(dateString: string)
    {
        const
            matches = this.getDateStringMatches(dateString);

        return !!matches;
    }
    public static fromDate(date?: Date)
    {
        date = date ?? new Date();

        const
            year = date.getFullYear(),
            month = date.getMonth() + 1,
            day = date.getDate(),
            hour = date.getHours(),
            minute = date.getMinutes(),
            second = date.getSeconds(),
            millisecond = date.getMilliseconds(),
            offset = this.getTimezoneOffset(year, month, day, hour, minute, second, millisecond);

        return new DateTimeInfo(
            year,
            month,
            day,
            hour,
            minute,
            second,
            millisecond,
            DateTimeInfoKind.local,
            offset
        );
    }
    public static fromDateString(dateString: string, kind?: DateTimeInfoKind): DateTimeInfo
    {
        const
            matches = this.getDateStringMatches(dateString);

        if (!matches)
            throw new Error("Invalid format");

        const
            year = parseInt(matches[1]),
            month = parseInt(matches[2]),
            day = parseInt(matches[3]),
            hour = matches[4] !== undefined ? parseInt(matches[4]) : undefined,
            minute = matches[5] !== undefined ? parseInt(matches[5]) : undefined,
            second = matches[6] !== undefined ? parseInt(matches[6]) : undefined,
            millisecond = matches[7] !== undefined ? truncateMilliseconds(parseInt(matches[7]), 3) : undefined,
            kindFinal = kind ?? (matches[8] === "Z" ? DateTimeInfoKind.utc : DateTimeInfoKind.local),
            offset =
                kindFinal === DateTimeInfoKind.utc
                    ? 0
                    :
                    (
                        matches[8] !== undefined
                            ? DateTimeInfo.getTimezoneOffsetInMinutes(matches[8] === "Z" ? "+00:00" : matches[8])
                            : (kindFinal === DateTimeInfoKind.local ? this.getTimezoneOffset(year, month, day, hour, minute, second, millisecond) : 0)
                    ),
            dateTime = new DateTimeInfo(
                year,
                month,
                day,
                hour,
                minute,
                second,
                millisecond,
                kindFinal,
                offset,
            );

        return (
            kindFinal === DateTimeInfoKind.utc
                ? dateTime.toUtc()
                : dateTime.toLocal()
        );
    }
    public static now()
    {
        return new DateTimeInfo();
    }
    public static tryFromDateString(dateString: string, kind?: DateTimeInfoKind): DateTimeInfo | false
    {
        try { return this.fromDateString(dateString, kind); }
        catch { /* Intentionally empty*/ }

        return false;
    }
    public static utcNow()
    {
        return new DateTimeInfo().toUtc();
    }
    public static utcToday()
    {
        const
            utcNow = this.utcNow();

        return new DateTimeInfo(utcNow.year, utcNow.month, utcNow.day, 0, 0, 0, 0, utcNow.kind);
    }

    // #endregion

    // #region Private Methods

    private static getDateStringMatches(dateString: string)
    {
        return /([0-9]{4})-([0-9]{2})-([0-9]{2})(?:(?:T| )([0-9]{2}):([0-9]{2})(?:(?::([0-9]{2}))?(?:\.([0-9]+))?)(Z|(?:\+|-)[0-9]{2}:[0-9]{2})?)?/.exec(dateString);
    }
    private static getTimezoneOffset(year: number, month: number, day: number, hour?: number, minute?: number, second?: number, millisecond?: number)
    {
        Assert.isNotNullOrUndefined(year, "year");
        Assert.isNotNullOrUndefined(month, "month");
        Assert.isNotNullOrUndefined(day, "day");

        return new Date(year, month - 1, day, hour ?? 0, minute ?? 0, second ?? 0, millisecond ?? 0).getTimezoneOffset();
    }
    private static getTimezoneOffsetAsString(offset: number | string)
    {
        const
            offsetInMinutes =
                typeof offset === "number"
                    ? offset
                    : DateTimeInfo.getTimezoneOffsetInMinutes(offset),
            timezone = {
                direction: offsetInMinutes > 0 ? "-" : "+",
                hours: Math.floor(Math.abs(offsetInMinutes) / 60),
                minutes: Math.abs(offsetInMinutes) % 60
            };

        return timezone.direction + timezone.hours.toFixed().padStart(2, "0") + ":" + timezone.minutes.toFixed().padStart(2, "0");
    }
    private static getTimezoneOffsetInMinutes(offset: number | string)
    {
        const
            offsetAsString =
                typeof offset === "string"
                    ? offset
                    : DateTimeInfo.getTimezoneOffsetAsString(offset),
            matches = /(\+|-)([0-9]{2}):([0-9]{2})/g.exec(offsetAsString);

        if (!matches)
            throw new Error("Invalid timezone offset format");

        return (parseInt(matches[2]) * 60 + parseInt(matches[3])) * (matches[1] === "+" ? -1 : 1);
    }
    private static getUpdatedDateTimeInfo(dateTimeInfo: DateTimeInfo, date: Date)
    {
        return new DateTimeInfo(
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds(),
            dateTimeInfo.kind,
            dateTimeInfo.offset
        );
    }
    private static validateDayOfMonth(day: number | undefined, month: number | undefined, year: number | undefined)
    {
        if (year === undefined || month === undefined || day === undefined)
            return;

        DateTimeInfo.validateNumberIsInRange("Month", month, 0, 12);

        const
            date = new Date(year, month - 1, day);

        if (date.getDate() !== day)
            throw new Error(`Day ${day} is not valid for ${Constants.Calendar.MONTH_NAMES[month]} ${year}`);
    }
    private static validateNumberIsInRange(parameterName: string, value: number | undefined, lowerLimit: number, upperLimit: number)
    {
        if (value && (value < lowerLimit || value > upperLimit))
            throw new Error(`${parameterName} must be in the range ${lowerLimit} to ${upperLimit}`);
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public readonly year: number;
    public readonly month: number;
    public readonly day: number;
    public readonly hour?: number;
    public readonly minute?: number;
    public readonly second?: number;
    public readonly millisecond?: number;
    public readonly offset: number;
    public readonly kind: DateTimeInfoKind;

    // #endregion

    // #region Constructor

    constructor()
    constructor(year: number, month: number, day: number, hour?: number, minute?: number, second?: number, millisecond?: number, kind?: DateTimeInfoKind, offset?: number | string)
    constructor(year?: number, month?: number, day?: number, hour?: number, minute?: number, second?: number, millisecond?: number, kind?: DateTimeInfoKind, offset?: number | string)
    {
        DateTimeInfo.validateNumberIsInRange("Month", month, 0, 12);
        DateTimeInfo.validateDayOfMonth(day, month, year);
        DateTimeInfo.validateNumberIsInRange("Hour", hour, 0, 23);
        DateTimeInfo.validateNumberIsInRange("Minute", minute, 0, 59);
        DateTimeInfo.validateNumberIsInRange("Second", second, 0, 59);
        DateTimeInfo.validateNumberIsInRange("Millisecond", millisecond, 0, 999);

        if (year === undefined || month === undefined || day === undefined)
        {
            const
                now = new Date();

            year = now.getFullYear();
            month = now.getMonth() + 1;
            day = now.getDate();
            hour = now.getHours();
            minute = now.getMinutes();
            second = now.getSeconds();
            millisecond = now.getMilliseconds();
        }

        this.year = year;
        this.month = month;
        this.day = day;

        if (hour !== undefined)
        {
            this.hour = hour;
            this.minute = minute ?? 0;
            this.second = second ?? 0;
            this.millisecond = millisecond ?? 0;
        }

        this.kind = kind ?? DateTimeInfoKind.local;

        const
            offsetInMinutes =
                offset !== undefined
                    ? (
                        typeof offset === "number"
                            ? offset
                            : DateTimeInfo.getTimezoneOffsetInMinutes(offset)
                    )
                    : undefined,
            fallbackOffset = DateTimeInfo.getTimezoneOffset(year, month, day, hour, minute, second, millisecond);

        this.offset =
            this.kind === DateTimeInfoKind.local
                ? (offsetInMinutes ?? fallbackOffset)
                : 0;
    }

    // #endregion

    // #region Public Methods

    public addDays(days: number)
    {
        if (!days)
            return this;

        const
            date = this.toDate();

        date.setDate(date.getDate() + days);

        return DateTimeInfo.getUpdatedDateTimeInfo(this, date);
    }
    public addHours(hours: number)
    {
        if (!hours)
            return this;

        const
            date = this.toDate(),
            hoursInMilliseconds = hours * 60 * 60 * 1000;

        date.setTime(date.getTime() + hoursInMilliseconds);

        return DateTimeInfo.getUpdatedDateTimeInfo(this, date);
    }
    public addMinutes(minutes: number)
    {
        if (!minutes)
            return this;

        const
            date = this.toDate(),
            minutesInMilliseconds = minutes * 60 * 1000;

        date.setTime(date.getTime() + minutesInMilliseconds);

        return DateTimeInfo.getUpdatedDateTimeInfo(this, date);
    }
    public addMonths(months: number)
    {
        if (!months)
            return this;

        const
            date = this.toDate();

        date.setMonth(date.getMonth() + months);

        return DateTimeInfo.getUpdatedDateTimeInfo(this, date);
    }
    public addSeconds(seconds: number)
    {
        if (!seconds)
            return this;

        const
            date = this.toDate(),
            secondsInMilliseconds = seconds * 1000;

        date.setTime(date.getTime() + secondsInMilliseconds);

        return DateTimeInfo.getUpdatedDateTimeInfo(this, date);
    }
    public addYears(years: number)
    {
        if (!years)
            return this;

        const
            date = this.toDate();

        date.setFullYear(date.getFullYear() + years);

        return DateTimeInfo.getUpdatedDateTimeInfo(this, date);
    }
    public compareTo(value: DateTimeInfo)
    {
        const
            thisAsStringAtUtc = this.toIsoString(),
            valueAsStringAtUtc = value.toIsoString();

        return thisAsStringAtUtc < valueAsStringAtUtc ? -1 : (thisAsStringAtUtc > valueAsStringAtUtc ? 1 : 0);
    }
    public equals(value: DateTimeInfo)
    {
        return this.compareTo(value) === 0;
    }
    public startOfMonth()
    {
        return this.day === 1 ? this : this.addDays((this.day - 1) * -1);
    }
    public toDate()
    {
        Assert.isNotNullOrUndefined(this.year, "this.year");
        Assert.isNotNullOrUndefined(this.month, "this.month");
        Assert.isNotNullOrUndefined(this.day, "this.day");

        return new Date(this.year, this.month - 1, this.day, this.hour ?? 0, this.minute ?? 0, this.second ?? 0, this.millisecond ?? 0);
    }
    public toIsoString()
    {
        return this.toUtc().toString("yyyy-MM-ddTHH:mm:ss.fffffffZ")
    }
    public toLocal()
    {
        return this.toOffset(DateTimeInfo.getTimezoneOffset(this.year, this.month, this.day, this.hour, this.minute, this.second, this.millisecond));
    }
    public toOffset(offset: number | string)
    {
        const
            dateTimeInfo =
                this.hour !== undefined
                    ?
                    (
                        this
                            .addMinutes(this.offset)
                            .addMinutes(DateTimeInfo.getTimezoneOffsetInMinutes(offset) * -1)
                    )
                    : this;

        return new DateTimeInfo(
            dateTimeInfo.year,
            dateTimeInfo.month,
            dateTimeInfo.day,
            dateTimeInfo.hour,
            dateTimeInfo.minute,
            dateTimeInfo.second,
            dateTimeInfo.millisecond,
            DateTimeInfoKind.local,
            offset
        );
    }
    public toString(): string
    public toString(format: string): string
    public toString(format?: string): string
    {
        if (format === undefined)
            format = "ddd MMM dd yyyy HH:mm:ss zzz";

        const
            date = this.toDate(),
            yearShort = parseInt(this.year.toFixed().substring(2)),
            hour = this.hour ?? 0,
            hour12 = hour === 0 ? 12 : (hour > 12 ? hour % 12 : hour),
            timezone = {
                direction: this.offset > 0 ? "-" : "+",
                hours: Math.floor(Math.abs(this.offset) / 60),
                minutes: Math.abs(this.offset) % 60
            };

        return (
            format.replace(/dddd|ddd|dd|d|fffffff|ffffff|fffff|ffff|fff|ff|f|gg|g|HH|H|hh|h|K|MMMM|MMM|MM|M|mm|m|ss|s|tt|t|yyyyy|yyyy|yyy|yy|y|zzz|zz|z/g, (match: string) =>
            {
                switch (match)
                {
                    case "dddd":
                        return date.toLocaleDateString(undefined, { weekday: "long" });
                    case "ddd":
                        return date.toLocaleDateString(undefined, { weekday: "short" });
                    case "dd":
                        return this.day.toFixed().padStart(2, "0");
                    case "d":
                        return this.day.toFixed();
                    case "fffffff":
                        return formatMilliseconds(this.millisecond ?? 0, 7);
                    case "ffffff":
                        return formatMilliseconds(this.millisecond ?? 0, 6);
                    case "fffff":
                        return formatMilliseconds(this.millisecond ?? 0, 5);
                    case "ffff":
                        return formatMilliseconds(this.millisecond ?? 0, 4);
                    case "fff":
                        return formatMilliseconds(this.millisecond ?? 0, 3);
                    case "ff":
                        return formatMilliseconds(this.millisecond ?? 0, 2);
                    case "f":
                        return formatMilliseconds(this.millisecond ?? 0, 1);
                    case "gg":
                    case "g":
                        return "A.D.";
                    case "HH":
                        return (this.hour ?? 0).toFixed().padStart(2, "0");
                    case "H":
                        return (this.hour ?? 0).toFixed();
                    case "hh":
                        return hour12.toFixed().padStart(2, "0");
                    case "h":
                        return hour12.toFixed();
                    case "K":
                        return (
                            this.kind === DateTimeInfoKind.local
                                ? DateTimeInfo.getTimezoneOffsetAsString(this.offset)
                                : "Z"
                        );
                    case "MMMM":
                        return date.toLocaleDateString(undefined, { month: "long" });
                    case "MMM":
                        return date.toLocaleDateString(undefined, { month: "short" });
                    case "MM":
                        return this.month.toFixed().padStart(2, "0");
                    case "M":
                        return this.month.toFixed();
                    case "mm":
                        return (this.minute ?? 0).toFixed().padStart(2, "0");
                    case "m":
                        return (this.minute ?? 0).toFixed();
                    case "ss":
                        return (this.second ?? 0).toFixed().padStart(2, "0");
                    case "s":
                        return (this.second ?? 0).toFixed();
                    case "tt":
                        return (hour < 12 ? "AM" : "PM");
                    case "t":
                        return (hour < 12 ? "A" : "P");
                    case "yyyyy":
                        return this.year.toFixed().padStart(5, "0");
                    case "yyyy":
                        return this.year.toFixed();
                    case "yyy":
                        return this.year.toFixed().padStart(3, "0");
                    case "yy":
                        return yearShort.toFixed().padStart(2, "0");
                    case "y":
                        return yearShort.toFixed();
                    case "zzz":
                        return DateTimeInfo.getTimezoneOffsetAsString(this.offset);
                    case "zz":
                        return timezone.direction + timezone.hours.toFixed().padStart(2, "0");
                    case "z":
                        return timezone.direction + timezone.hours.toFixed();
                }

                return StringHelper.empty;
            })
        );
    }
    public toUtc()
    {
        if (this.kind === DateTimeInfoKind.utc)
            return this;

        const
            dateTimeInfo = this.toOffset(0);

        return new DateTimeInfo(
            dateTimeInfo.year,
            dateTimeInfo.month,
            dateTimeInfo.day,
            dateTimeInfo.hour,
            dateTimeInfo.minute,
            dateTimeInfo.second,
            dateTimeInfo.millisecond,
            DateTimeInfoKind.utc
        );
    }

    // #endregion

    // #endregion
}
